import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * GooglePhoneTracker component
 * 
 * This component handles Google Ads website call conversion tracking in a React SPA.
 * It re-triggers Google's phone number replacement after each route change.
 * 
 * How it works:
 * 1. Google's gtag.js scans the DOM for the business phone number (1800 233 023)
 * 2. For visitors from Google Ads, it replaces the number with a Google forwarding number
 * 3. Calls to the forwarding number are tracked as conversions and still route to the business
 * 4. In a SPA, we need to re-trigger this scan after navigation since content changes dynamically
 */
const GooglePhoneTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Re-trigger Google's phone number replacement after route changes
    // This is necessary for React SPA where content loads dynamically
    const triggerPhoneReplacement = () => {
      if (typeof window !== 'undefined' && window.google_trackConversion) {
        // Legacy method - some implementations use this
        try {
          window.google_trackConversion();
        } catch (e) {
          // Silently fail if not available
        }
      }
      
      // Modern gtag.js method - dispatch a custom event that gtag listens for
      // gtag.js automatically re-scans when certain conditions are met
      if (typeof window !== 'undefined' && window.gtag) {
        // Force gtag to re-evaluate the page
        window.gtag('event', 'page_view', {
          page_path: location.pathname,
          page_title: document.title
        });
      }
      
      // Alternative: Trigger MutationObserver-based re-scan
      // Google's phone replacement script uses MutationObserver to detect DOM changes
      // We can trigger it by briefly modifying a tracked element
      const phoneElements = document.querySelectorAll('[data-phone-number]');
      phoneElements.forEach(el => {
        // Trigger a minimal DOM mutation to activate Google's observer
        el.setAttribute('data-phone-scanned', Date.now().toString());
      });
    };

    // Small delay to ensure DOM is fully rendered after navigation
    const timeoutId = setTimeout(triggerPhoneReplacement, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  return null;
};

export default GooglePhoneTracker;
